import React from 'react';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';

const badgeToneClassName = {
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-400/20 dark:text-yellow-300',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-400/20 dark:text-orange-300'
};

const ServiceCard = ({ title, body, cta, tone, badge, badgeTone, onClick }) => {
  const tones = {
    violet:
      'bg-[#F3E8FF] text-[#6D28D9] hover:bg-[#E9D5FF] dark:bg-violet-400/15 dark:text-violet-200 dark:hover:bg-violet-400/25',
    orange:
      'bg-[#FFF1E7] text-[#C2410C] hover:bg-[#FFE4D1] dark:bg-orange-400/15 dark:text-orange-200 dark:hover:bg-orange-400/25',
    teal:
      'bg-[#DDF5EC] text-[#0F766E] hover:bg-[#C9EFE3] dark:bg-teal-400/15 dark:text-teal-200 dark:hover:bg-teal-400/25',
    blue:
      'bg-[#E8F1FF] text-[#1D4ED8] hover:bg-[#D9E8FF] dark:bg-sky-400/15 dark:text-sky-200 dark:hover:bg-sky-400/25'
  };
  const buttonTone = tones[tone] || tones.violet;

  return (
    <article
      className="relative flex h-full flex-col rounded-2xl border border-border bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition hover:-translate-y-0.5 hover:border-accent/25 hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)] sm:p-4 dark:bg-panel-strong dark:shadow-[0_8px_28px_rgba(0,0,0,0.28)] dark:hover:border-accent/35 dark:hover:bg-panel"
    >
      {badge ? (
        <span
          className={classNames(
            'absolute -top-2.5 end-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold shadow-sm sm:end-3 sm:px-2.5 sm:py-1',
            badgeToneClassName[badgeTone] || badgeToneClassName.yellow
          )}
        >
          {badge}
        </span>
      ) : null}

      <h3 className="min-w-0 text-sm font-semibold leading-5 text-text sm:text-base">{title}</h3>
      <p className="mt-1.5 hidden flex-1 text-sm leading-6 text-muted sm:mt-2 sm:block">{body}</p>
      <button
        type="button"
        onClick={onClick}
        className={classNames(
          'mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-semibold transition sm:mt-5 sm:px-3 sm:py-2.5 sm:text-sm',
          buttonTone
        )}
      >
        {cta}
        <ArrowLeft size={14} />
      </button>
    </article>
  );
};

export const RismanServicesSection = ({ isTemporary = false, className } = {}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const openService = (path, state) => {
    if (isAuthenticated) {
      navigate(path, state ? { state } : undefined);
      return;
    }

    navigate('/login', {
      state: {
        redirectTo: path,
        ...(state || {})
      }
    });
  };

  const services = [
    {
      key: 'chat',
      title: t('homeServiceChatTitle'),
      body: t('homeServiceChatBody'),
      cta: t('homeServiceChatCta'),
      tone: 'violet',
      onClick: () => openService(isTemporary ? '/chat?temporary=1' : '/chat')
    },
    {
      key: 'code',
      title: t('homeServiceCodeTitle'),
      body: t('homeServiceCodeBody'),
      cta: t('homeServiceCodeCta'),
      tone: 'teal',
      badge: t('special'),
      badgeTone: 'orange',
      onClick: () => openService('/projects', { openCreateProject: true })
    },
    {
      key: 'image',
      title: t('homeServiceImageTitle'),
      body: t('homeServiceImageBody'),
      cta: t('homeServiceImageCta'),
      tone: 'orange',
      onClick: () => openService('/images')
    },
    {
      key: 'cloud',
      title: t('homeServiceCloudTitle'),
      body: t('homeServiceCloudBody'),
      cta: t('homeServiceCloudCta'),
      tone: 'blue',
      badge: t('new'),
      badgeTone: 'yellow',
      onClick: () => openService('/cloud')
    }
  ];

  return (
    <section className={classNames(className)} aria-labelledby="risman-services-title">
      <div className="mb-5 text-center sm:text-start">
        <h2
          id="risman-services-title"
          className="inline-flex items-center gap-2 text-base font-bold text-text"
        >
          <Sparkles size={16} className="text-[#8B5CF6] dark:text-violet-300" />
          {t('homeServicesTitle')}
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {services.map((service) => (
          <ServiceCard key={service.key} {...service} />
        ))}
      </div>
    </section>
  );
};
